import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Home } from 'lucide-react';
import type { BreadcrumbItemDto } from '../../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../../application/hooks/useTranslation';

export interface TeamBreadcrumbProps {
  path: BreadcrumbItemDto[]; // viewer → focus
  onSelect: (index: number) => void;
}

/** Sticky "My Team › … › Focus" trail; middle crumbs collapse to "…" when the path is long. */
export function TeamBreadcrumb({ path, onSelect }: TeamBreadcrumbProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const lastId = path[path.length - 1]?.id;

  // keep the current level in view when the trail is wider than the screen
  useEffect(() => {
    const el = navRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [lastId, expanded]);

  if (path.length === 0) return null;
  const collapse = !expanded && path.length > 3;
  const shown = collapse ? [0, path.length - 2, path.length - 1] : path.map((_, i) => i);

  return (
    <nav ref={navRef} className="tm-crumbs" aria-label={t('team.pathLabel')} data-testid="team-breadcrumb">
      <ol>
        {shown.map((i, pos) => {
          const item = path[i];
          const last = i === path.length - 1;
          return (
            <li key={item.id}>
              {pos > 0 && <ChevronRight size={13} className="tm-crumb-sep" aria-hidden />}
              {collapse && pos === 1 && (
                <>
                  <button type="button" className="tm-crumb tm-crumb--more" onClick={() => setExpanded(true)} aria-label="Show full path">
                    …
                  </button>
                  <ChevronRight size={13} className="tm-crumb-sep" aria-hidden />
                </>
              )}
              <button
                type="button"
                className={`tm-crumb${last ? ' is-current' : ''}`}
                onClick={() => onSelect(i)}
                aria-current={last ? 'page' : undefined}
                data-testid="team-crumb"
              >
                {i === 0 ? (
                  <>
                    <Home size={13} aria-hidden /> {t('team.myTeam')}
                  </>
                ) : (
                  <>
                    {item.fullName} <span className="tm-crumb-tier">{item.tier}</span>
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
